import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  type InstanceRow,
  getInterventionDisplayLines,
  getInterventionPhaseUiKey,
  isTerminalInterventionStatus,
} from '../../components/interventions/instanceModel';
import { defaultPriorityForPhase } from '../mapUtils';

interface UseInterventionFiltersParams {
  activeInterventions: InstanceRow[];
  userId: number | undefined;
  interventionSearch: string;
  interventionPhaseFilter: 'all' | 'wait' | 'travel' | 'site';
  interventionSort: 'priority' | 'newest' | 'oldest';
  setInterventions: React.Dispatch<React.SetStateAction<InstanceRow[]>>;
}

export function useInterventionFilters({
  activeInterventions,
  userId,
  interventionSearch,
  interventionPhaseFilter,
  interventionSort,
  setInterventions,
}: UseInterventionFiltersParams) {
  const [priorityOverrides, setPriorityOverrides] = useState<Record<number, number>>({});

  useEffect(() => {
    if (!userId) return;
    try {
      const raw = localStorage.getItem(`mapInterventionPriorities:${userId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, number>;
      const normalized: Record<number, number> = {};
      Object.entries(parsed).forEach(([id, val]) => {
        const numericId = Number(id);
        const numericVal = Number(val);
        if (
          Number.isInteger(numericId) &&
          numericId > 0 &&
          Number.isFinite(numericVal) &&
          numericVal > 0
        ) {
          normalized[numericId] = Math.floor(numericVal);
        }
      });
      setPriorityOverrides(normalized);
    } catch {
      // Ignore localStorage parse errors.
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    setPriorityOverrides((prev) => {
      const activeIds = new Set(activeInterventions.map((inv) => inv.id));
      const next: Record<number, number> = {};
      Object.entries(prev).forEach(([idStr, value]) => {
        const id = Number(idStr);
        if (activeIds.has(id)) next[id] = value;
      });
      localStorage.setItem(`mapInterventionPriorities:${userId}`, JSON.stringify(next));
      return next;
    });
  }, [activeInterventions, userId]);

  const getInterventionPriority = useCallback(
    (inv: InstanceRow): number => {
      const override = priorityOverrides[inv.id];
      if (Number.isFinite(override) && override > 0) return Math.floor(override);
      const fromRow = Number(inv.priorite);
      if (Number.isFinite(fromRow) && fromRow > 0) return Math.floor(fromRow);
      return defaultPriorityForPhase(getInterventionPhaseUiKey(inv));
    },
    [priorityOverrides],
  );

  const setInterventionPriority = useCallback(
    (inv: InstanceRow, nextPriorityRaw: number) => {
      if (!userId) return;
      if (isTerminalInterventionStatus(inv.statut || '')) return;
      const nextPriority = Math.max(1, Math.floor(Number(nextPriorityRaw) || 1));
      setPriorityOverrides((prev) => {
        const next = { ...prev, [inv.id]: nextPriority };
        localStorage.setItem(`mapInterventionPriorities:${userId}`, JSON.stringify(next));
        return next;
      });
      setInterventions((prev) =>
        prev.map((row) => (row.id === inv.id ? { ...row, priorite: nextPriority } : row)),
      );
    },
    [userId, setInterventions],
  );

  const filteredInterventions = useMemo(() => {
    const normalizedSearch = interventionSearch.trim().toLowerCase();
    const rows = activeInterventions.filter((inv) => {
      const phase = getInterventionPhaseUiKey(inv);
      if (interventionPhaseFilter !== 'all' && phase !== interventionPhaseFilter) return false;
      if (!normalizedSearch) return true;
      const lines = getInterventionDisplayLines(inv, 'Intervention');
      return (
        lines.headline.toLowerCase().includes(normalizedSearch) ||
        lines.subline.toLowerCase().includes(normalizedSearch) ||
        String(inv.numero_intervention || '').toLowerCase().includes(normalizedSearch)
      );
    });
    rows.sort((a, b) => {
      if (interventionSort === 'priority') {
        const pa = getInterventionPriority(a);
        const pb = getInterventionPriority(b);
        if (pa !== pb) return pa - pb;
      }
      const ta = a.date_creation ? new Date(a.date_creation).getTime() : 0;
      const tb = b.date_creation ? new Date(b.date_creation).getTime() : 0;
      if (interventionSort === 'oldest') return ta - tb;
      return tb - ta;
    });
    return rows;
  }, [
    activeInterventions,
    interventionSearch,
    interventionPhaseFilter,
    interventionSort,
    getInterventionPriority,
  ]);

  const interventionPhaseCounts = useMemo(() => {
    const counts = { wait: 0, travel: 0, site: 0 };
    activeInterventions.forEach((inv) => {
      const phase = getInterventionPhaseUiKey(inv);
      if (phase === 'wait' || phase === 'travel' || phase === 'site') counts[phase] += 1;
    });
    return counts;
  }, [activeInterventions]);

  return {
    priorityOverrides,
    getInterventionPriority,
    setInterventionPriority,
    filteredInterventions,
    interventionPhaseCounts,
  };
}
